import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { RestApiService } from '../api/rest-api.service';

import {
  USER_ENDPOINTS,
} from '../api/api-endpoints';

export interface OnboardingProfileDraft {
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  gender: string | null;
}

export interface OnboardingContactDraft {
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  emergency_contact: string | null;
}

export interface OnboardingStatus {
  profile_completed: boolean;
  contact_completed: boolean;
  password_setup_required: boolean;
  password_setup_completed: boolean;
  needs_organisation_setup: boolean;
  completed: boolean;
  next_step: string | null;
  profile: OnboardingProfileDraft;
  contact: OnboardingContactDraft;
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private statusCache: OnboardingStatus | null = null;
  private statusFetchedAt = 0;
  private readonly cacheTtlMs = 5000;
  private readonly onboardingRootRoute = '/onboarding';
  private readonly organisationSetupRoute = '/onboarding/organisation-setup';
  private readonly additionalInformationRoute = '/onboarding/additional-info';
  private readonly contactInformationRoute = '/onboarding/contact-info';
  private readonly passwordSetupRoute = '/onboarding/password-setup';

  constructor(private readonly restApi: RestApiService) {}

  async getStatus(forceRefresh = false): Promise<OnboardingStatus> {
    const now = Date.now();
    if (
      !forceRefresh &&
      this.statusCache &&
      now - this.statusFetchedAt < this.cacheTtlMs
    ) {
      return this.statusCache;
    }

    const status: any = await firstValueFrom(
      this.restApi.get(USER_ENDPOINTS.onboarding_status)
    );
    const normalized = this.normalizeStatus(status);
    this.statusCache = normalized;
    this.statusFetchedAt = now;
    return normalized;
  }

  async saveProfile(payload: {
    first_name: string;
    middle_name?: string | null;
    last_name: string;
    date_of_birth: string;
    gender?: string | null;
  }): Promise<OnboardingStatus> {
    const response: any = await firstValueFrom(
      this.restApi.post(USER_ENDPOINTS.onboarding_profile, payload)
    );
    const normalized = this.normalizeStatus(response);
    this.statusCache = normalized;
    this.statusFetchedAt = Date.now();
    return normalized;
  }

  async saveContact(payload: {
    email: string;
    phone: string;
    address: string;
    city: string;
    region?: string | null;
    country: string;
    emergency_contact?: string | null;
  }): Promise<OnboardingStatus> {
    const response: any = await firstValueFrom(
      this.restApi.post(USER_ENDPOINTS.onboarding_contact, payload)
    );
    const normalized = this.normalizeStatus(response);
    this.statusCache = normalized;
    this.statusFetchedAt = Date.now();
    return normalized;
  }

  invalidateCache(): void {
    this.statusCache = null;
    this.statusFetchedAt = 0;
  }

  needsAdditionalInformation(
    status: OnboardingStatus | null | undefined
  ): boolean {
    return (
      !!status &&
      !status.completed &&
      !status.needs_organisation_setup &&
      !status.profile_completed
    );
  }

  needsOrganisationSetup(status: OnboardingStatus | null | undefined): boolean {
    return !!status && !status.completed && status.needs_organisation_setup;
  }

  needsContactInformation(status: OnboardingStatus | null | undefined): boolean {
    return (
      !!status &&
      !status.completed &&
      !status.needs_organisation_setup &&
      status.profile_completed &&
      !status.contact_completed
    );
  }

  needsPasswordSetup(status: OnboardingStatus | null | undefined): boolean {
    return (
      !!status &&
      !status.completed &&
      !status.needs_organisation_setup &&
      status.profile_completed &&
      status.contact_completed &&
      status.password_setup_required &&
      !status.password_setup_completed
    );
  }

  getNextOnboardingRoute(
    status: OnboardingStatus | null | undefined
  ): string | null {
    if (!status || status.completed) {
      return null;
    }

    if (this.needsOrganisationSetup(status)) {
      return this.organisationSetupRoute;
    }

    if (this.needsAdditionalInformation(status)) {
      return this.additionalInformationRoute;
    }

    if (this.needsContactInformation(status)) {
      return this.contactInformationRoute;
    }

    if (this.needsPasswordSetup(status)) {
      return this.passwordSetupRoute;
    }

    const fallbackRoute = this.normalizePath(status.next_step);
    return this.isOnboardingRoute(fallbackRoute) ? fallbackRoute : null;
  }

  normalizePath(url: string | null | undefined): string {
    const raw = `${url ?? ''}`.trim();
    if (!raw) {
      return '/';
    }

    const [pathWithQueryRemoved] = raw.split('?');
    const [path] = pathWithQueryRemoved.split('#');
    const normalized = path.trim();

    if (!normalized) {
      return '/';
    }

    return normalized.startsWith('/') ? normalized : `/${normalized}`;
  }

  isOnboardingRoute(url: string | null | undefined): boolean {
    return this.normalizePath(url).startsWith(this.onboardingRootRoute);
  }

  private normalizeStatus(value: any): OnboardingStatus {
    return {
      profile_completed: !!value?.profile_completed,
      contact_completed: !!value?.contact_completed,
      password_setup_required: !!value?.password_setup_required,
      password_setup_completed: !!value?.password_setup_completed,
      needs_organisation_setup: !!value?.needs_organisation_setup,
      completed: !!value?.completed,
      next_step:
        typeof value?.next_step === 'string' && value.next_step.trim().length > 0
          ? value.next_step
          : null,
      profile: {
        first_name: this.asOptionalString(value?.profile?.first_name),
        middle_name: this.asOptionalString(value?.profile?.middle_name),
        last_name: this.asOptionalString(value?.profile?.last_name),
        date_of_birth: this.asOptionalString(value?.profile?.date_of_birth),
        gender: this.asOptionalString(value?.profile?.gender),
      },
      contact: {
        email: this.asOptionalString(value?.contact?.email),
        phone: this.asOptionalString(value?.contact?.phone),
        address: this.asOptionalString(value?.contact?.address),
        city: this.asOptionalString(value?.contact?.city),
        region: this.asOptionalString(value?.contact?.region),
        country: this.asOptionalString(value?.contact?.country),
        emergency_contact: this.asOptionalString(value?.contact?.emergency_contact),
      },
    };
  }

  private asOptionalString(value: unknown): string | null {
    const text = `${value ?? ''}`.trim();
    return text.length > 0 ? text : null;
  }
}
